import { useState } from "react";
import { FlaskConical, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COPY = {
  delete: {
    title: (n) => `Delete ${n} lead${n > 1 ? "s" : ""}?`,
    body: "This permanently removes the selected leads and everything linked to them: watch and reading time, downloads, chat history and hub answers. This can't be undone.",
    btn: "Delete permanently",
    done: (n) => `Deleted ${n} lead${n > 1 ? "s" : ""}`,
  },
  reset_time: {
    title: (n) => `Reset watch time for ${n} lead${n > 1 ? "s" : ""}?`,
    body: "The leads and their form answers stay, but all their viewing and reading sessions (time, completion, pages, downloads) are permanently cleared.",
    btn: "Reset watch time",
    done: (n) => `Watch time reset for ${n} lead${n > 1 ? "s" : ""}`,
  },
};

export const LeadBulkActions = ({ campaignId, selected, leads = [], onDone }) => {
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const n = selected.length;

  const markable = leads.filter((l) => selected.includes(l.id));
  const allMarked = markable.length > 0 && markable.every((l) => l.test_marked);
  const mark = async () => {
    const act = allMarked ? "unmark_test" : "mark_test";
    try {
      const r = await api.post(`/campaigns/${campaignId}/leads/bulk`, { lead_ids: selected, action: act });
      toast.success(act === "mark_test" ? `Marked ${r.data.count} as test` : `Unmarked ${r.data.count} test lead${r.data.count > 1 ? "s" : ""}`);
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/campaigns/${campaignId}/leads/bulk`, { lead_ids: selected, action });
      toast.success(COPY[action].done(r.data.count));
      setAction(null);
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const btn = "h-9 px-4 rounded-full text-sm flex items-center gap-2 border transition-colors disabled:opacity-40 disabled:pointer-events-none";
  return (
    <div className="flex items-center gap-2" data-testid="lead-bulk-actions">
      {n > 0 && <span data-testid="lead-selected-count" className="font-mono text-xs text-slate-400 mr-1">{n} selected</span>}
      <button type="button" data-testid="lead-mark-test-button" disabled={!n} onClick={mark}
        className={`${btn} border-rose-500/30 text-rose-300 hover:bg-rose-500/10`}><FlaskConical className="w-3.5 h-3.5" /> {allMarked ? "Unmark test" : "Mark as test"}</button>
      <button type="button" data-testid="lead-reset-time-button" disabled={!n} onClick={() => setAction("reset_time")}
        className={`${btn} border-[#242C3D] text-slate-300 hover:text-white hover:bg-white/5`}><RotateCcw className="w-3.5 h-3.5" /> Reset watch time</button>
      <button type="button" data-testid="lead-delete-button" disabled={!n} onClick={() => setAction("delete")}
        className={`${btn} border-red-500/40 text-red-300 hover:bg-red-500/10`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      <AlertDialog open={!!action} onOpenChange={(o) => !o && !busy && setAction(null)}>
        {action && (
          <AlertDialogContent data-testid="lead-confirm-dialog" className="bg-[#141A24] border-[#242C3D] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="lead-confirm-title">{COPY[action].title(n)}</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">{COPY[action].body}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="lead-confirm-cancel" disabled={busy} className="bg-transparent border-[#242C3D] text-slate-300 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="lead-confirm-submit" disabled={busy} onClick={(e) => { e.preventDefault(); run(); }}
                className={action === "delete" ? "bg-red-600 hover:bg-red-500" : "bg-indigo-500 hover:bg-indigo-400"}>
                {busy ? "Working…" : COPY[action].btn}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </div>
  );
};
